/**
 * useDeferredComponent — يُحمِّل مكوّناً من حزمة منفصلة **بعد** العرض الأوّل.
 *
 * لماذا: حزمة NewWeatherMap ‏533 ك.ب تُحلَّل وتُنفَّذ كاملةً قبل أوّل إطار، وفيها أدوات لا
 * يراها أغلب الزوّار عند الدخول (الحرائق، أداة القياس، لوحة الإعدادات). فصلها يخفّف الحزمة
 * الأولى، و«التسخين» عند أوّل فراغ للمتصفّح يجعلها جاهزة قبل أن يضغط أحدٌ زرّها — فلا
 * تأخير عند الفتح. وإن طُلبت قبل التسخين (needNow) تُحمَّل فوراً.
 *
 * لماذا لا React.lazy وحده: lazy يبدأ التحميل لحظة **رسم** المكوّن، وهذه المكوّنات تُرسم
 * دائماً (تُخفي نفسها داخلياً) — فكان الفصل سيُلغى عملياً ويُحمَّل كل شيء عند التركيب.
 */
import { useEffect, useState, type ComponentType } from 'react';

type Loader<P> = () => Promise<ComponentType<P>>;

const cache = new Map<Loader<never>, ComponentType<never>>();

function whenIdle(cb: () => void, delayMs: number): () => void {
    const ric = (window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
    });
    let idleId: number | null = null;
    const t = window.setTimeout(() => {
        if (ric.requestIdleCallback) idleId = ric.requestIdleCallback(cb, { timeout: 4000 });
        else cb();
    }, delayMs);
    return () => {
        window.clearTimeout(t);
        if (idleId !== null) ric.cancelIdleCallback?.(idleId);
    };
}

export function useDeferredComponent<P>(loader: Loader<P>, needNow = false, delayMs = 3000): ComponentType<P> | null {
    const [Comp, setComp] = useState<ComponentType<P> | null>(
        () => (cache.get(loader as Loader<never>) as ComponentType<P> | undefined) ?? null);

    useEffect(() => {
        if (Comp) return;
        let cancelled = false;
        const load = () => {
            loader().then((c) => {
                cache.set(loader as Loader<never>, c as ComponentType<never>);
                // setState بدالّة: المكوّن نفسه دالّة، فتمريره مباشرةً يجعل React ينفّذه كمحدِّث.
                if (!cancelled) setComp(() => c);
            }).catch(() => { /* شبكة متعثّرة: يُعاد عند الحاجة التالية */ });
        };
        if (needNow) { load(); return () => { cancelled = true; }; }
        const cancelIdle = whenIdle(load, delayMs);
        return () => { cancelled = true; cancelIdle(); };
    }, [Comp, loader, needNow, delayMs]);

    return Comp;
}
